/**
 * Iter 100 — Admin Patient Education Library.
 *
 * Lets clinic admins/clinicians curate informational videos (anatomy,
 * ergonomics, recovery, etc) for their patients. Videos can be:
 *   • Clinic-only (private)
 *   • Public (shared platform-wide)
 *
 * Auto-attach engine fires when a HEP is created (backend hook) — videos
 * whose body_regions overlap with the HEP's are queued in each patient's
 * portal automatically.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import {
  BookOpen, Plus, Pencil, Trash2, Eye, Video, Tag, Globe,
  CheckCircle2, X,
} from 'lucide-react';
import { safeConfirm } from '../../lib/confirm';

const CATEGORIES = [
  { key: 'anatomy',           label: 'Anatomy' },
  { key: 'ergonomics',        label: 'Ergonomics' },
  { key: 'mental_health',     label: 'Mental health' },
  { key: 'recovery',          label: 'Recovery' },
  { key: 'disease_education', label: 'Disease education' },
  { key: 'exercise_primer',   label: 'Exercise primer' },
];

const BODY_REGIONS = ['cervical', 'shoulder', 'thoracic', 'lumbar', 'hip', 'knee', 'ankle', 'wrist', 'elbow', 'foot'];

export default function AdminEducation() {
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ category: '', body_region: '' });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (filter.category) params.category = filter.category;
    if (filter.body_region) params.body_region = filter.body_region;
    const [r1, r2] = await Promise.all([
      api.get('/education/videos', { params }),
      api.get('/education/stats').catch(() => ({ data: null })),
    ]);
    setVideos(r1.data.videos);
    setStats(r2.data);
  };
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (data) => {
    setBusy(true);
    try {
      if (editing?.video_id) {
        await api.put(`/education/videos/${editing.video_id}`, data);
      } else {
        await api.post('/education/videos', data);
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!await safeConfirm('Delete this video? All patient assignments will be removed too.')) return;
    await api.delete(`/education/videos/${id}`);
    await load();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="admin-education-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2 inline-flex items-center gap-1">
            <BookOpen className="w-3 h-3"/> PATIENT EDUCATION
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Education library</h1>
          <p className="text-fg-muted text-sm mt-1">Curate informational videos auto-attached to patient home programs by body region.</p>
        </div>
        <button onClick={() => { setEditing({}); setShowForm(true); }}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="edu-new-btn">
          <Plus className="w-4 h-4"/> Add a video
        </button>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid sm:grid-cols-4 gap-3 mb-6" data-testid="edu-stats">
          <StatTile icon={Video} value={stats.total_videos} label="Videos in library"/>
          <StatTile icon={Eye} value={stats.total_assigned} label="Total assignments"/>
          <StatTile icon={CheckCircle2} value={stats.total_completed} label="Completions"/>
          <StatTile icon={Tag} value={`${stats.completion_pct}%`} label="Completion rate"/>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select className="input md:w-48" value={filter.category} onChange={e => setFilter(f => ({ ...f, category: e.target.value }))} data-testid="edu-filter-category">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select className="input md:w-48" value={filter.body_region} onChange={e => setFilter(f => ({ ...f, body_region: e.target.value }))} data-testid="edu-filter-region">
          <option value="">All body regions</option>
          {BODY_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Videos grid */}
      {videos.length === 0 ? (
        <div className="surface border border-soft rounded-xl p-10 text-center" data-testid="edu-empty">
          <BookOpen className="w-12 h-12 text-fg-muted/40 mx-auto mb-3"/>
          <p className="font-semibold mb-1">No videos yet</p>
          <p className="text-sm text-fg-muted mb-4">Build your education library — patients will see relevant videos auto-attached to their care plan.</p>
          <button onClick={() => { setEditing({}); setShowForm(true); }} className="btn-primary">Add your first video</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="edu-grid">
          {videos.map(v => (
            <div key={v.video_id} className="surface border border-soft rounded-xl p-4 hover:border-brand transition" data-testid={`edu-row-${v.video_id}`}>
              <div className="aspect-video bg-bg-secondary rounded mb-3 flex items-center justify-center overflow-hidden">
                {v.thumbnail_url
                  ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover"/>
                  : <Video className="w-8 h-8 text-fg-muted/40"/>}
              </div>
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-heading font-semibold text-sm flex-1 leading-tight">{v.title}</h3>
                {v.is_public && <Globe className="w-3.5 h-3.5 text-[#1B5E20] shrink-0" aria-label="Public"/>}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-fg-muted mb-2">
                <span className="bg-brand/10 text-brand px-1.5 py-0.5 rounded font-semibold">{v.category}</span>
                {v.duration_sec && <span>{Math.floor(v.duration_sec / 60)}m</span>}
                <span>· {v.view_count || 0} views</span>
              </div>
              {v.body_regions?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {v.body_regions.map(r => <span key={r} className="text-[10px] bg-fg-muted/10 px-1.5 py-0.5 rounded">{r}</span>)}
                </div>
              )}
              <div className="flex justify-end gap-1">
                <button onClick={() => { setEditing(v); setShowForm(true); }}
                        className="btn-secondary text-xs px-2 py-1 inline-flex items-center gap-1"
                        data-testid={`edu-edit-${v.video_id}`}>
                  <Pencil className="w-3 h-3"/> Edit
                </button>
                <button onClick={() => remove(v.video_id)}
                        className="text-red-600 hover:bg-red-50 rounded text-xs px-2 py-1 inline-flex items-center gap-1"
                        data-testid={`edu-delete-${v.video_id}`}>
                  <Trash2 className="w-3 h-3"/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <EducationVideoForm
          initial={editing}
          onSave={save}
          onClose={() => { setShowForm(false); setEditing(null); }}
          busy={busy}/>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, value, label }) {
  return (
    <div className="surface border border-soft rounded-xl p-3">
      <Icon className="w-4 h-4 text-brand mb-2"/>
      <div className="text-2xl font-heading font-bold leading-none">{value ?? '—'}</div>
      <div className="text-xs text-fg-muted mt-1">{label}</div>
    </div>
  );
}

function EducationVideoForm({ initial, onSave, onClose, busy }) {
  const [form, setForm] = useState({
    title: initial?.title || '',
    description: initial?.description || '',
    video_url: initial?.video_url || '',
    thumbnail_url: initial?.thumbnail_url || '',
    category: initial?.category || 'anatomy',
    tags: (initial?.tags || []).join(', '),
    body_regions: initial?.body_regions || [],
    duration_sec: initial?.duration_sec || '',
    is_public: initial?.is_public || false,
    language: initial?.language || 'en',
  });
  const submit = (e) => {
    e.preventDefault();
    if (!form.title || !form.video_url) {
      alert('Title and video URL are required.');
      return;
    }
    onSave({
      ...form,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      duration_sec: form.duration_sec ? parseInt(form.duration_sec) : null,
    });
  };
  const toggleRegion = (r) => {
    setForm(f => ({
      ...f,
      body_regions: f.body_regions.includes(r)
        ? f.body_regions.filter(x => x !== r)
        : [...f.body_regions, r],
    }));
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form className="bg-bg-secondary rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()} onSubmit={submit}
            data-testid="edu-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-lg">{initial?.video_id ? 'Edit video' : 'Add a video'}</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium">Title *</span>
            <input className="input mt-1" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required data-testid="edu-form-title"/>
          </label>
          <label className="block">
            <span className="text-xs font-medium">Video URL * (YouTube / Vimeo / direct MP4)</span>
            <input className="input mt-1" value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} required data-testid="edu-form-url"/>
          </label>
          <label className="block">
            <span className="text-xs font-medium">Thumbnail URL</span>
            <input className="input mt-1" value={form.thumbnail_url} onChange={e => setForm(f => ({ ...f, thumbnail_url: e.target.value }))}/>
          </label>
          <label className="block">
            <span className="text-xs font-medium">Description</span>
            <textarea className="input mt-1" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}/>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium">Category</span>
              <select className="input mt-1" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="edu-form-category">
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium">Duration (seconds)</span>
              <input type="number" className="input mt-1" value={form.duration_sec} onChange={e => setForm(f => ({ ...f, duration_sec: e.target.value }))}/>
            </label>
          </div>
          <div>
            <span className="text-xs font-medium block mb-1.5">Body regions <em className="text-fg-muted">(drives auto-attach to HEPs)</em></span>
            <div className="flex flex-wrap gap-1">
              {BODY_REGIONS.map(r => (
                <button key={r} type="button" onClick={() => toggleRegion(r)}
                        className={`text-xs px-2 py-1 rounded border ${form.body_regions.includes(r) ? 'bg-brand text-white border-brand' : 'border-soft hover:border-brand'}`}
                        data-testid={`edu-region-${r}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-xs font-medium">Tags <em className="text-fg-muted">(comma-separated keywords)</em></span>
            <input className="input mt-1" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="post-op, sciatica, beginner"/>
          </label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_public} onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))}/>
            <span>Share publicly across all Physiogenic clinics</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="edu-form-save">
            {busy ? 'Saving…' : 'Save video'}
          </button>
        </div>
      </form>
    </div>
  );
}
